import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Text, Alert, Icon, AlertDescription, IconButton, Tooltip, Spinner, ModalBody } from "@chakra-ui/react";
import { ClassroomOverviewDTO } from "@/stores/classroomStore_I";
import { User, getUserDisplayTextForSelect } from "@/js/utils/userUtils";
import { envConfig } from "@/config";
import { colours } from "@/utils/themeUtils";
import { StudentSearchSelector } from "./StudentSearchSelector";
import { useToast } from "@/components/ui/fzcl/Toast";
import { toasts } from "@/utils/themeUtils";
import { userDisplayText } from "@/js/utils/userUtils";
import { UserInfo } from "@/js/api/authApi";
import { getUserDetail } from "@/js/api/usersApi";
import { getClassroomDisplayName, isClassroomSynced } from "@/utils/classroomUtils";
import { addStudentToClassroom, removeStudentFromClassroom } from "@/api/modifyStudents";
import { useQueryClient } from "@tanstack/react-query";
import { makeClassroomDetailsInvalidationKey } from "@/js/query/useClassroomDetailsQuery";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { getClassroomDetails } from "@/js/api/classApi";
import { postClassroomModifiedEvent } from "@/api/legacyBackendApi";
import { ClasswizeModal } from "./ui/Modal";

interface Props {
	isOpen: boolean;
	onClose: () => void;

	isMonitoringClass: boolean;
	students?: User[];
	classroom: ClassroomOverviewDTO;

	/**
	 * Optional callback - Allows the parent component to perform an action when the students are modified (such as fetching fresh data).
	 */
	studentsModifiedCallback?: (addedStudents: User[], removedStudents: User[]) => void;
}

export function ModifyStudentsModal(props: Props): JSX.Element {
	const toast = useToast();

	const [selectedStudents, setSelectedStudents] = useState<User[]>(props.students ?? []);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const [isLoadingStudents, setIsLoadingStudents] = useState<boolean>(false);

	const userSessionStore = useUserSessionStore();
	const teacherId = userSessionStore.getTeacherId() || "";

	const queryClient = useQueryClient();

	useEffect(() => {
		// We don't have the students to pass as a prop from on the overview page.
		// We also don't want to fetch all the students for all the classes when not needed.
		// So this will fetch the students for the class if they're not already provided and only when the modal is open.
		if (!props.isOpen) return;

		// Fetch students if they're not provided
		if (!props.students) {
			setIsLoadingStudents(true);
			getClassroomDetails(
				props.classroom.id || props.classroom.name,
				true,
				false,
				false,
				false,
				props.classroom.type,
				props.classroom.sourceId,
				useUserSessionStore.getState().getTeacherId()
			)
				.then((c) => {
					setSelectedStudents(c.classroom.students ?? []);
				})
				.finally(() => {
					setIsLoadingStudents(false);
				});
		}
	}, [props.isOpen, props.classroom.id, props.classroom.name, props.classroom.sourceId, props.classroom.type, props.students]);

	// Update selected students when props.students changes
	useEffect(() => {
		setSelectedStudents(props.students ?? []);
	}, [props.students]);

	function handleStudentsModified(addedStudents: UserInfo[], removedStudents: UserInfo[]) {
		if (props.studentsModifiedCallback) {
			props.studentsModifiedCallback(addedStudents, removedStudents);
		}

		const added: string[] = addedStudents.map((user) => user.username);
		const removed: string[] = removedStudents.map((user) => user.username);

		// Update the list of classroom users for the active classroom.
		queryClient.invalidateQueries(makeClassroomDetailsInvalidationKey(props.classroom.id));

		postClassroomModifiedEvent({
			teacher: userSessionStore.getActiveUser() as string,
			classroomName: props.classroom.name as string,
			studentsAdded: added,
			studentsRemoved: removed,
		});
	}

	function handleAddStudent(student: UserInfo) {
		if (props.isMonitoringClass && selectedStudents.length >= envConfig.MONITOR_MODE_MAX_STUDENTS) {
			toast.errorToast({
				title: `You can only add ${envConfig.MONITOR_MODE_MAX_STUDENTS} students in monitoring mode`,
				position: toasts.position,
			});
			return;
		}

		if (selectedStudents.some((s) => s.stableIdV1 === student.stableIdV1)) {
			toast.infoToast({
				title: `${userDisplayText(student)} is already in ${getClassroomDisplayName(props.classroom)}`,
				position: toasts.position,
			});
			return;
		}

		setIsSaving(true);

		getUserDetail(student.username, false, false, true)
			.then((user) => {
				if (!user) {
					toast.errorToast({
						title: `Failed to add ${userDisplayText(student)} to ${getClassroomDisplayName(props.classroom)}`,
						description: "Please try again or contact support if the issue persists.",
						position: toasts.position,
					});
					setIsSaving(false);
					return;
				}

				if (!user.isStudent) {
					// The `isStudent` property will be false if the user is a teacher, or if they are added to the list of "restricted users" in School Manager.
					toast.errorToast({
						title: `Could not add ${userDisplayText(student)} to ${getClassroomDisplayName(props.classroom)}`,
						description: `${userDisplayText(student)} cannot be added to classes as a student because they are either a teacher or a restricted user.`,
						position: toasts.position,
					});
					setIsSaving(false);
					return;
				}

				addStudentToClassroom(teacherId, user.id, props.classroom.id, props.classroom.type.toLowerCase() as "classroom" | "group")
					.then(() => {
						setSelectedStudents((prev) => [...prev, student]);
						handleStudentsModified([student], []);
						toast.successToast({
							isClosable: true,
							title: `${userDisplayText(student)} successfully added to ${getClassroomDisplayName(props.classroom)}`,
							position: toasts.position,
						});
					})
					.catch((error) => {
						console.error("Failed to add student to class:", error);
						toast.errorToast({
							title: `Failed to add ${userDisplayText(student)} to ${getClassroomDisplayName(props.classroom)}`,
							description: "Please try again or contact support if the issue persists.",
							position: toasts.position,
						});
					});
			})
			.catch((error) => {
				console.error("Failed to add student to class:", error);
				toast.errorToast({
					title: `Failed to add ${userDisplayText(student)} to ${getClassroomDisplayName(props.classroom)}`,
					description: "Please try again or contact support if the issue persists.",
					position: toasts.position,
				});
				return;
			})
			.finally(() => {
				setIsSaving(false);
			});
	}

	function handleRemoveStudent(student: User) {
		setIsSaving(true);

		getUserDetail(student.username, false, false, true)
			.then((user) => {
				if (!user) {
					toast.errorToast({
						title: `Failed to add ${userDisplayText(student)} to ${getClassroomDisplayName(props.classroom)}`,
						description: "Please try again or contact support if the issue persists.",
						position: toasts.position,
					});
					setIsSaving(false);
					return;
				}

				removeStudentFromClassroom(
					teacherId,
					user.id,
					user.stableIdV1,
					user.username,
					props.classroom.type.toLowerCase() as "classroom" | "group",
					props.classroom.id,
					props.classroom.stableIdV1
				)
					.then(() => {
						setSelectedStudents((prev) => prev.filter((s) => s.stableIdV1 !== user.stableIdV1));
						handleStudentsModified([], [user]);
						toast.successToast({
							isClosable: true,
							title: `${userDisplayText(user)} successfully removed from ${getClassroomDisplayName(props.classroom)}`,
							position: toasts.position,
						});
					})
					.catch((error) => {
						console.error("Failed to remove student from class:", error);
						toast.errorToast({
							title: `Failed to remove ${userDisplayText(user)} from ${getClassroomDisplayName(props.classroom)}`,
							description: "Please try again or contact support if the issue persists.",
							position: toasts.position,
						});
					})
					.finally(() => {
						setIsSaving(false);
					});
			})
			.catch((error) => {
				console.error("Failed to add student to class:", error);
				toast.errorToast({
					title: `Failed to add ${userDisplayText(student)} to ${getClassroomDisplayName(props.classroom)}`,
					description: "Please try again or contact support if the issue persists.",
					position: toasts.position,
				});
				return;
			});
	}

	const sortedStudents = useMemo(() => {
		return [...selectedStudents].sort((a, b) => a.username.localeCompare(b.username));
	}, [selectedStudents]);

	return (
		<ClasswizeModal
			isOpen={props.isOpen}
			onClose={props.onClose}
			size={"2xl"}
			title={`Modify Students`}
			dataTestId={"modify-students-modal"}
		>
			<ModalBody>
				<Flex direction={"column"} gap={6}>
					<Text color={colours.navy[800]} lineHeight={"20px"}>
						Add or remove students from this class. Classwize will not alert students that they are being monitored.
					</Text>

					{isClassroomSynced(props.classroom) && (
						<Text fontSize={"14px"} color={colours.danger.icon} lineHeight={"20px"} fontWeight={500}>
							These settings may be overridden when users are synced
						</Text>
					)}

					{selectedStudents.length > envConfig.MAX_STUDENTS && (
						<Flex>
							<Alert status="warning" bgColor="#FFE8EA" mb="16px" pt="12px" width="unset">
								<Box mx="8px" pt="-4px">
									<Icon className="fa-solid fa-triangle-exclamation" color="#FF0000" />
								</Box>
								<AlertDescription fontSize="14px" fontWeight="500" pt="2px">
									Total students: {selectedStudents.length}. Classes can have a maximum of {envConfig.MAX_STUDENTS} students. This class
									will not be able to be started until the number of students is within the limit.
								</AlertDescription>
							</Alert>
						</Flex>
					)}

					{isLoadingStudents ? (
						<Flex alignItems="center" justifyContent="center" gap={2.5} my={2.5}>
							<Spinner />
							<Text color={colours.text.body} fontSize={"14px"} lineHeight={"20px"}>
								Loading students...
							</Text>
						</Flex>
					) : (
						<>
							<Flex alignItems={"center"} justifyContent={"space-between"} mb={-5} px={1}>
								<Text fontWeight="medium" color={"neutrals.800"}>
									Students
								</Text>
								{props.isMonitoringClass && (
									<Text color={"neutrals.300"}>
										<Text
											as="span"
											fontWeight={selectedStudents.length > envConfig.MONITOR_MODE_MAX_STUDENTS ? 500 : 400}
											color={selectedStudents.length > envConfig.MONITOR_MODE_MAX_STUDENTS ? colours.danger.icon : "inherit"}
										>
											{selectedStudents.length}
										</Text>
										/{envConfig.MONITOR_MODE_MAX_STUDENTS} selected
									</Text>
								)}
							</Flex>
							<StudentSearchSelector selectStudent={handleAddStudent} />
							<Box maxHeight={"300px"} overflowY={"auto"} mt={-4}>
								{!selectedStudents || selectedStudents.length === 0 ? (
									<Flex alignItems="center" justifyContent="center" gap={2.5} my={2.5}>
										<Text color={colours.blueGray[600]}>Get started by adding students to your class</Text>
									</Flex>
								) : (
									<>
										{sortedStudents.map((student) => (
											<Flex
												key={student.stableIdV1}
												alignItems="center"
												justifyContent="space-between"
												py={2}
												px={3}
												_hover={{ bgColor: "#F7F9FC" }}
												rounded={"md"}
												transition={"background-color 0.2s"}
											>
												<Text fontSize={"14px"} color={colours.text.body}>
													{getUserDisplayTextForSelect(student)}
												</Text>
												<Tooltip
													label={`Remove ${userDisplayText(student)} from class`}
													aria-label={`Remove ${userDisplayText(student)} from class`}
												>
													<IconButton
														data-testid={`remove-student-button-${student.username}`}
														onClick={() => {
															handleRemoveStudent(student);
														}}
														aria-label="Remove student"
														icon={<Icon className="fa-solid fa-x" />}
														bgColor="transparent"
														color={colours.danger.icon}
														size="sm"
														variant="unstyled"
														isDisabled={isSaving}
													/>
												</Tooltip>
											</Flex>
										))}
									</>
								)}
							</Box>
						</>
					)}
				</Flex>
			</ModalBody>
		</ClasswizeModal>
	);
}
